import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { FaMicrophone, FaCog, FaImage, FaWindowClose } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import WorkoutList from '../components/workout/WorkoutList';
import WorkoutSummaryImage from '../components/workout/WorkoutSummaryImage';
import { isTwoHandDumbbellExercise, formatWeight, formatReps, formatDuration } from '../utils/weightFormatter';

// 안드로이드 네이티브에서 오디오 데이터를 받는 콜백 함수
window.onNativeAudio = function(dataUrl) {
  if (window.processNativeAudioCallback && typeof window.processNativeAudioCallback === 'function') {
    window.processNativeAudioCallback(dataUrl);
  } else {
    console.error("❌ 오디오 처리 콜백 함수가 없거나 유효하지 않음");
  }
};

// 컴포넌트 외부에서 바로 체크하는 웹뷰 감지 함수
const isProbablyIOSWebView = () => {
  // 네이티브에서 설정한 플래그 확인
  if (window.isInIOSWebView === true) {
    return true;
  }
  
  // 메시지 핸들러 'logging' 확인 (네이티브 코드가 추가한 핸들러)
  return !!(window.webkit && 
            window.webkit.messageHandlers);
};

// 앱 시작 시 바로 웹뷰 확인 - 컴포넌트 렌더링 전에도 확인 가능
if (window.isInIOSWebView || isProbablyIOSWebView()) {
  // 전역 플래그 설정 - 다른 코드에서 사용 가능
  window.isInIOSWebView = true;
}

// 웹뷰 감지 이벤트 리스너
if (typeof window.addEventListener === 'function') {
  window.addEventListener('ios-webview-detected', () => {
    window.isInIOSWebView = true;
    window.__iOSWebView__ = true;
  });
}

// 중복 실행 방지 및 iOS 웹뷰 감지
if (!window.isInIOSWebView) {
  window.__iosWebViewInjected__ = true;
  window.isInIOSWebView = true;
  window.dispatchEvent(new CustomEvent("ios-webview-detected"));
}

const HomePage = () => {
  // Auth Context에서 사용자 정보 및 공통 API 클라이언트 가져오기
  // useAuth에서 isAdminMode, isGroupOwnerMode 가져오기
  const { getCurrentUser, api, isAdminMode, isGroupOwnerMode } = useAuth();
  // 가장 모드를 고려한 현재 사용자 정보 가져오기
  const currentUser = getCurrentUser();
  
  // 상태 관리
  const [workoutStage, setWorkoutStage] = useState('start'); // start, record, confirm, rest, finish
  const [menuOpen, setMenuOpen] = useState(false);
  const [workoutData, setWorkoutData] = useState(null);
  const [recognizedText, setRecognizedText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [restTime, setRestTime] = useState(60); // 기본 60초
  const [remainingTime, setRemainingTime] = useState(0);
  const [isResting, setIsResting] = useState(false);
  const [workouts, setWorkouts] = useState([]);
  const [currentSet, setCurrentSet] = useState(0);
  const [currentWorkoutId, setCurrentWorkoutId] = useState(null);
  const [editingWorkout, setEditingWorkout] = useState(null);
  const [editingSet, setEditingSet] = useState(null);
  const [isEditingRestTime, setIsEditingRestTime] = useState(false); // 추가: 휴식 시간 편집 중 상태
  const [restTimeInput, setRestTimeInput] = useState('60'); // 추가: 휴식 시간 입력값
  const [isLoading, setIsLoading] = useState(false);
  const [transcriptionResult, setTranscriptionResult] = useState(null);
  const [error, setError] = useState(null);
  const [previousExerciseName, setPreviousExerciseName] = useState('');
  // 백엔드에서 받은 운동 데이터를 임시 저장할 상태 변수
  const [tempWorkoutData, setTempWorkoutData] = useState(null);
  // 요약 이미지 표시 여부
  const [showSummaryCard, setShowSummaryCard] = useState(false);
  const [showSummaryImage, setShowSummaryImage] = useState(false); // 새로 추가된 상태 변수

  // 안드로이드 웹뷰 감지를 위한 상태 추가
  const [isWebViewMode, setIsWebViewMode] = useState(false);
  
  const timerRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  // API 호출 중복 방지를 위한 ref
  const isFetchingRef = useRef(false);
  // 마지막 API 호출 시간 추적을 위한 ref
  const lastFetchTimeRef = useRef(0);
  // 디바운스 타이머 추적을 위한 ref
  const debounceTimerRef = useRef(null);

  // 프로덕션 모드인지 확인하는 함수
  const isProduction = () => process.env.NODE_ENV === 'production';

  // 컴포넌트 마운트 시 웹뷰 환경 감지
  useEffect(() => {
    const handler = () => {
      setIsWebViewMode(true);
      setupIOSMessageHandlers();
    };
    window.addEventListener('ios-webview-detected', handler);
    
    // iOS 웹뷰 메시지 핸들러 설정
    const setupIOSMessageHandlers = () => {
      // iOS에서 네이티브 코드가 호출할 함수 정의
      window.handleNativeAudioData = (audioData) => {
        // iOS에서 오디오 처리 중 상태로 설정
        if (window.VoiceFitState) {
          window.VoiceFitState.isIOSProcessingAudio = true;
        }
        processNativeAudio(audioData);
      };
      
      // notifyAudioEnded 메시지 수신 처리
      window.notifyAudioEnded = () => {
        setIsRecording(false);
        
        // 아직 로딩 중이라면 (오디오 데이터가 아직 오지 않음) 일정 시간 후 로딩 상태 초기화
        if (isLoading) {
          setTimeout(() => {
            if (isLoading) {
              setIsLoading(false);
              setError("녹음된 오디오 데이터를 수신하지 못했습니다.");
            }
          }, 5000); // 5초 후 확인
        }
      };
      
      // 사용 가능한 iOS 메시지 핸들러 확인
      const availableHandlers = [];
      if (window.webkit && window.webkit.messageHandlers) {
        if (window.webkit.messageHandlers.receiveAudio) availableHandlers.push('receiveAudio');
        if (window.webkit.messageHandlers.startRecording) availableHandlers.push('startRecording');
        if (window.webkit.messageHandlers.stopRecording) availableHandlers.push('stopRecording');
      }
    };
    
    // 이미 웹뷰로 감지된 경우
    if (window.isInIOSWebView) {
      setIsWebViewMode(true);
      setupIOSMessageHandlers();
    }

    return () => {
      window.removeEventListener('ios-webview-detected', handler);
    };
  }, []);

  // 오디오 리소스 정리를 위한 useEffect 추가
  useEffect(() => {
    return () => {
      // 모든 타이머 정리
      if (window.maxRecordingTimer) clearTimeout(window.maxRecordingTimer);
      if (timerRef.current) clearInterval(timerRef.current);
      
      // 오디오 리소스 정리
      cleanupAudioResources();
    };
  }, []);

  // 오디오 리소스 정리 함수
  const cleanupAudioResources = () => {
    if (mediaRecorderRef.current) {
      try {
        // 이전 녹음이 진행 중이라면 중지
        if (mediaRecorderRef.current.state === 'recording') {
          mediaRecorderRef.current.stop();
        }
        
        // 스트림이 있다면 트랙 정리
        if (mediaRecorderRef.current.stream) {
          mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
        }
      } catch (e) {
        console.warn("오디오 리소스 정리 중 오류:", e);
      }
    }
    
    // 참조 초기화
    mediaRecorderRef.current = null;
    audioChunksRef.current = [];
  };

  // 안드로이드 웹뷰 환경인지 확인하는 함수
  const isAndroidWebView = () => {
    const result = !!(window.Android && typeof window.Android.startMic === 'function');
    return result;
  };

  // iOS 웹뷰 환경인지 확인하는 함수
  const isIOSWebView = () => {
    // 전역으로 설정된 플래그 확인
    if (window.isInIOSWebView === true) {
      return true;
    }
    
    // 네이티브가 주입한 플래그 확인 (가장 확실한 방법)
    if (window.__iOSWebView__ === true) {
      return true;
    }
    
    // isProbablyIOSWebView 함수 재사용
    if (isProbablyIOSWebView()) {
      // 발견 시 전역 플래그도 설정 (다른 함수에서 재사용)
      window.isInIOSWebView = true;
      return true;
    }
    
    // Safari 브라우저인지 확인 (웹뷰가 아닌 경우 체크)
    if (navigator.vendor && navigator.vendor.indexOf('Apple') > -1 &&
        navigator.userAgent.indexOf('Safari') > -1 &&
        navigator.userAgent.indexOf('Chrome') === -1) {
      // Safari 브라우저 (웹뷰 아님)
    }
    
    return false;
  };

  // 마이크 권한 요청 및 녹음 시작 함수
  const requestMicPermission = () => {
    // 확실하게 웹뷰인 경우만 네이티브 코드 호출
    const isRealAndroidWebView = isAndroidWebView(); 
    // 메시지 핸들러가 실제로 있는 경우만 iOS 웹뷰로 인식
    const isRealIOSWebView = window.webkit && 
                            window.webkit.messageHandlers && 
                            Object.keys(window.webkit.messageHandlers).length > 0;
    
    // 안드로이드 웹뷰 환경인지 체크
    if (isRealAndroidWebView) {
      // 안드로이드 웹뷰 환경에서는 네이티브 마이크 사용
      
      // 오디오 처리 콜백 설정
      window.processNativeAudioCallback = processNativeAudio;
      
      try {
        // 녹음 상태 UI 업데이트
        setIsRecording(true);
        
        // 네이티브 마이크 녹음 시작 (안드로이드에서 3초 후 자동 종료됨)
        window.Android.startMic();
      } catch (err) {
        console.error("❌ 네이티브 마이크 호출 실패:", err);
        // 오류 상태 표시
        setError('녹음 중지 중 오류가 발생했습니다');
      }
    } else if (isRealIOSWebView) {
      // iOS 웹뷰 환경에서는 iOS 네이티브에 녹음 요청
      
      try {
        // startRecording 핸들러가 있는지 확인
        if (window.webkit && 
            window.webkit.messageHandlers && 
            window.webkit.messageHandlers.startRecording) {
          // 정식 핸들러가 있는 경우
          window.webkit.messageHandlers.startRecording.postMessage({});
          
          // 녹음 상태 UI 업데이트
          setIsRecording(true);
        } else if (window.webkit && window.webkit.messageHandlers) {
          // 사용 가능한 핸들러 확인
          const availableHandlers = Object.keys(window.webkit.messageHandlers);
          
          if (availableHandlers.length > 0) {
            const firstHandler = availableHandlers[0];
            window.webkit.messageHandlers[firstHandler].postMessage({action: 'startRecording'});
            
            // 녹음 상태 UI 업데이트
            setIsRecording(true);
          } else {
            startMicRecording();
          }
        } else {
          startMicRecording();
        }
      } catch (err) {
        console.error("❌ iOS 네이티브 녹음 요청 실패:", err);
        setError('iOS 마이크 기능을 사용할 수 없습니다. 웹 API로 시도합니다.');
        // 네이티브 실패 시 웹 API 시도
        startMicRecording();
      }
    } else {
      // 일반 웹 환경에서는 Web API 사용
      startMicRecording();
    }
  };

  // 웹 API 사용하여 마이크 녹음 시작
  const startMicRecording = () => {
    // 가장 최근 세트 찾아서 운동 이름 가져오기
    let latestExerciseName = previousExerciseName;
    
    // previousExerciseName이 이미 설정되어 있는 경우에만 사용 (휴식 시작 버튼을 누른 경우에만)
    // workouts에서 최근 운동 이름을 가져오는 로직은 제거
    
    // 이전 녹음 세션 정리
    cleanupAudioResources();
    
    // 최대 녹음 시간 타이머 존재 시 정리
    if (window.maxRecordingTimer) {
      clearTimeout(window.maxRecordingTimer);
      window.maxRecordingTimer = null;
    }
    
    // iOS Safari 및 웹뷰에서 필요한 추가 제약 조건
    const constraints = {
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      }
    };
    
    // 일정 시간이 지나도 녹음이 시작되지 않으면 상태 초기화
    const timeoutId = setTimeout(() => {
      if (isRecording) {
        setIsRecording(false);
        setError('녹음을 시작할 수 없습니다. 다시 시도해주세요.');
      }
    }, 5000); // 5초 후 확인
    
    navigator.mediaDevices.getUserMedia(constraints)
      .then((stream) => {
        // 마이크 연결 성공
        try {
          // iOS에서는 MediaRecorder 지원이 제한적일 수 있으므로 오디오 형식 명시
          const options = { mimeType: 'audio/webm;codecs=opus' };
          
          // iOS에서 지원하지 않는 경우 fallback
          if (!MediaRecorder.isTypeSupported(options.mimeType)) {
            mediaRecorderRef.current = new MediaRecorder(stream);
          } else {
            mediaRecorderRef.current = new MediaRecorder(stream, options);
          }
          
          audioChunksRef.current = []; // 청크 초기화

          // ondataavailable 이벤트가 정기적으로 발생하도록 설정 (iOS에서 중요)
          mediaRecorderRef.current.ondataavailable = (event) => {
            if (event.data && event.data.size > 0) {
              audioChunksRef.current.push(event.data);
            }
          };

          mediaRecorderRef.current.onstop = async () => {
            // 타임아웃 취소
            clearTimeout(timeoutId);
            
            // 최대 녹음 시간 타이머 존재 시 정리
            if (window.maxRecordingTimer) {
              clearTimeout(window.maxRecordingTimer);
              window.maxRecordingTimer = null;
            }
            
            // 녹음 상태 업데이트
            setIsRecording(false);
            
            try {
              // 오디오 데이터 청크가 있는지 확인
              if (!audioChunksRef.current.length) {
                throw new Error("녹음된 오디오 데이터가 없습니다");
              }
              
              // 오디오 형식 확인 후 적절한 MIME 타입 설정
              const mimeType = mediaRecorderRef.current.mimeType || 'audio/mp4';
              
              // 빠른 처리를 위해 작업 시작 즉시 스트림 종료
              stream.getTracks().forEach(track => track.stop());
              
              // 데이터 처리 전 상태 업데이트 (UI 반응성 개선)
              setIsLoading(true);
              
              // 비동기 작업을 Promise로 감싸서 처리 성능 향상
              const blobPromise = new Promise(resolve => {
                // 작은 지연을 통해 UI 스레드 차단 방지
                setTimeout(() => {
                  const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
                  resolve(audioBlob);
                }, 10);
              });
              
              // Blob 생성 대기
              const audioBlob = await blobPromise;
              
              // 백엔드로 데이터 전송
              await sendAudioToBackend(audioBlob, latestExerciseName);
            } catch (err) {
              console.error("❌ 녹음 데이터 처리 중 오류:", err);
              setError('녹음 데이터 처리 중 오류가 발생했습니다: ' + (err.message || ''));
              setIsLoading(false);
            } finally {
              // 사용이 끝난 mediaRecorder 참조 정리
              mediaRecorderRef.current = null;
            }
          };
          
          // 녹음 도중 오류 발생 시 핸들러
          mediaRecorderRef.current.onerror = (event) => {
            console.error("❌ MediaRecorder 오류:", event);
            // 타임아웃 취소
            clearTimeout(timeoutId);
            // 녹음 상태 초기화
            setIsRecording(false);
            setError('녹음 중 오류가 발생했습니다.');
            // 스트림 트랙 중지
            stream.getTracks().forEach(track => track.stop());
            // 참조 초기화
            mediaRecorderRef.current = null;
          };

          // 녹음 시작을 화면에 표시하도록 상태 업데이트
          setIsRecording(true);
          
          // 실제 녹음 시작
          mediaRecorderRef.current.start(1000); // 1초마다 데이터 요청
          
          // iOS와 Safari에서는 최대 녹음 시간 제한
          const maxRecordingTime = 15000; // 15초
          
          // 기존 타이머 제거
          if (window.maxRecordingTimer) {
            clearTimeout(window.maxRecordingTimer);
          }
          
          // 전역 변수에 타이머 저장 (다른 함수에서도 접근 가능하도록)
          window.maxRecordingTimer = setTimeout(() => {
            // 녹음 중인지 다시 확인
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              try {
                mediaRecorderRef.current.stop();
              } catch (e) {
                console.error("❌ 자동 녹음 종료 오류:", e);
                setIsRecording(false);
                
                // 스트림 정리
                if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
                  mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
                }
                
                // 참조 초기화
                mediaRecorderRef.current = null;
              }
            }
            
            // 타이머 자체도 정리
            window.maxRecordingTimer = null;
          }, maxRecordingTime);
        } catch (err) {
          console.error("❌ MediaRecorder 생성 오류:", err);
          // 타임아웃 취소
          clearTimeout(timeoutId);
          // 상태 업데이트
          setIsRecording(false);
          setError('녹음 기능이 지원되지 않는 브라우저입니다.');
          
          // 스트림 트랙 중지
          stream.getTracks().forEach(track => track.stop());
        }
      })
      .catch((err) => {
        console.error("❌ 마이크 접근 또는 녹음 시작 오류:", err);
        // 타임아웃 취소
        clearTimeout(timeoutId);
        // 상태 업데이트
        setIsRecording(false);
        setError('마이크 접근 권한이 필요하거나 지원되지 않는 브라우저입니다.');
      });
  };

  // 네이티브에서 전달받은 오디오 데이터 처리
  const processNativeAudio = (dataUrl) => {
    try {
      // 녹음 상태 업데이트
      setIsRecording(false);
      
      // dataUrl이 실제 문자열인지 확인 (iOS에서 가끔 객체로 전달되는 경우가 있음)
      if (typeof dataUrl !== 'string') {
        console.error("❌ dataUrl이 문자열이 아님:", typeof dataUrl);
        if (dataUrl && dataUrl.toString) {
          dataUrl = dataUrl.toString();
        } else {
          throw new Error('유효하지 않은 dataUrl 형식 (문자열이 아님)');
        }
      }
      
      // iOS에서 전달된 JSON 객체일 수 있는 경우 처리
      if (dataUrl.startsWith('{') && dataUrl.endsWith('}')) {
        try {
          const jsonData = JSON.parse(dataUrl);
          
          // JSON 객체에서 데이터 URL 추출
          if (jsonData.audioData) {
            dataUrl = jsonData.audioData;
          } else {
            throw new Error('JSON 객체에 audioData 필드가 없음');
          }
        } catch (jsonErr) {
          console.error("❌ JSON 파싱 오류:", jsonErr);
          // JSON 파싱 실패했지만 계속 진행 (문자열 데이터일 수 있음)
        }
      }
      
      // dataUrl에서 base64 부분과 MIME 타입 추출 (data:audio/mp4;base64,XXXX 형식)
      const matches = dataUrl.match(/^data:(audio\/[^;]+);base64,(.+)$/);
      
      if (!matches || matches.length !== 3) {
        console.error("❌ dataUrl 형식 불일치:", dataUrl.substring(0, 50) + "...");
        
        // iOS에서 base64 데이터만 보내는 경우 처리
        if (/^[A-Za-z0-9+/=]+$/.test(dataUrl)) {
          // iOS는 주로 m4a 포맷을 사용하므로 기본값으로 설정
          const mimeType = "audio/m4a";
          const base64Data = dataUrl;
          
          // base64를 Blob으로 변환
          try {
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Array(byteCharacters.length);
            
            for (let i = 0; i < byteCharacters.length; i++) {
              byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            
            const byteArray = new Uint8Array(byteNumbers);
            
            if (byteArray.length < 10) {
              throw new Error('오디오 데이터가 너무 짧습니다');
            }
            
            // 오디오 Blob 생성 및 전송
            const audioBlob = new Blob([byteArray], { type: mimeType });
            
            // 백엔드로 오디오 데이터 전송
            sendAudioToBackend(audioBlob, previousExerciseName);
            return; // 함수 종료
          } catch (e) {
            console.error("❌ base64 변환 오류:", e);
            throw new Error('base64 데이터 처리 실패');
          }
        } else {
          throw new Error('유효하지 않은 dataUrl 형식');
        }
      }
      
      const mimeType = matches[1]; // audio/mp4, audio/3gpp 등
      const base64Data = matches[2];
      
      // base64를 Blob으로 변환
      let byteCharacters;
      try {
        byteCharacters = atob(base64Data);
      } catch (e) {
        console.error("❌ base64 디코딩 오류:", e);
        throw new Error('base64 디코딩 실패');
      }
      
      const byteNumbers = new Array(byteCharacters.length);
      
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      
      const byteArray = new Uint8Array(byteNumbers);
      
      // 오디오 데이터 유효성 확인 (파일 헤더 체크)
      if (byteArray.length < 10) {
        console.error("❌ 오디오 데이터가 너무 짧음:", byteArray.length);
        throw new Error('오디오 데이터가 너무 짧습니다');
      }
      
      // 원본 MIME 타입 그대로 사용 (변환하지 않음)
      const audioBlob = new Blob([byteArray], { type: mimeType });
      
      // 백엔드로 오디오 데이터 전송
      sendAudioToBackend(audioBlob, previousExerciseName);
    } catch (err) {
      console.error("❌ 네이티브 오디오 데이터 처리 오류:", err);
      setIsRecording(false);
      setError('오디오 데이터 처리 중 오류가 발생했습니다: ' + (err.message || '알 수 없는 오류'));
    }
  };

  // 마이크 버튼 클릭 처리
  const handleMicrophoneClick = () => {
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
    
    // 현재 처리 중인 경우 중복 클릭 방지
    if (isLoading) {
      return;
    }
    
    if (isRecording) {
      // 확실하게 웹뷰인 경우만 네이티브 코드 호출
      const isRealAndroidWebView = isAndroidWebView();
      // 메시지 핸들러가 실제로 있는 경우만 iOS 웹뷰로 인식
      const isRealIOSWebView = window.webkit && 
                              window.webkit.messageHandlers && 
                              Object.keys(window.webkit.messageHandlers).length > 0;
      
      // 녹음 중지 시작 시 즉시 상태 업데이트 (UI 반응성 개선)
      setIsRecording(false);
      
      // 안드로이드 웹뷰 환경인 경우 Android.stopMic() 호출
      if (isRealAndroidWebView) {
        try {
          window.Android.stopMic();
        } catch (err) {
          console.error("❌ 안드로이드 녹음 중지 오류:", err);
          // 오류 상태 표시
          setError('녹음 중지 중 오류가 발생했습니다');
          setIsLoading(false);
        }
      } 
      // iOS 웹뷰 환경인 경우 stopRecording 메시지 전송 또는 웹 API 사용
      else if (isRealIOSWebView) {
        try {
          // stopRecording 핸들러가 있는지 확인
          const hasStopRecordingHandler = window.webkit && 
              window.webkit.messageHandlers && 
              window.webkit.messageHandlers.stopRecording;
          
          // 사용 가능한 핸들러 체크
          const availableHandlers = window.webkit && window.webkit.messageHandlers ? 
            Object.keys(window.webkit.messageHandlers) : [];
          
          // 네이티브 메시지 핸들러 사용 가능 여부
          let usedNativeHandler = false;
            
          if (hasStopRecordingHandler) {
            // 정식 핸들러 사용
            try {
              window.webkit.messageHandlers.stopRecording.postMessage({});
              usedNativeHandler = true;
            } catch (handlerErr) {
              console.error("❌ stopRecording 핸들러 호출 실패:", handlerErr);
            }
          } else if (availableHandlers.length > 0) {
            // 대체 핸들러 사용
            const firstHandler = availableHandlers[0];
            try {
              window.webkit.messageHandlers[firstHandler].postMessage({action: 'stopRecording'});
              usedNativeHandler = true;
            } catch (handlerErr) {
              console.error(`❌ ${firstHandler} 핸들러 호출 실패:`, handlerErr);
            }
          }
          
          // 네이티브 핸들러를 사용하지 않았으면 웹 API로 전환
          if (!usedNativeHandler) {
            if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
              try {
                mediaRecorderRef.current.stop();
              } catch (err) {
                console.error("❌ MediaRecorder 중지 오류:", err);
                if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
                  mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
                }
                setIsLoading(false);
              }
            } else {
              setIsLoading(false);
            }
          }
        } catch (err) {
          console.error("❌ iOS 녹음 중지 오류:", err);
          // 오류 상태 표시
          setError('iOS 녹음 중지 중 오류가 발생했습니다');
          
          // 로딩 상태 해제
          setIsLoading(false);
        }
      }
      // 일반 웹 환경에서는 mediaRecorder.stop() 호출
      else if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
        try {
          mediaRecorderRef.current.stop();
        } catch (err) {
          console.error("❌ MediaRecorder 중지 오류:", err);
          
          // 스트림이 있으면 트랙 중지
          if (mediaRecorderRef.current && mediaRecorderRef.current.stream) {
            mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
          }
          setIsLoading(false);
        }
      } else {
        // MediaRecorder가 없거나 녹음 중이 아닌 상태에서는 상태만 초기화
        setIsLoading(false);
      }
    } else {
      // 녹음 시작
      setError(null); // 이전 에러 초기화
      requestMicPermission(); // 마이크 권한 요청 및 녹음 시작
    }
  };

  // 사운드 재생 함수
  const playSound = () => {
    if (audioRef.current) {
      try {
        audioRef.current.currentTime = 0; // 재생 위치 초기화
        const playPromise = audioRef.current.play();
        
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn("⚠️ 알람 재생 실패:", error);
          });
        }
      } catch (e) {
        console.error("❌ 알람 재생 오류:", e);
      }
    }
  };

  // 백엔드로 오디오 데이터 전송 (공통 API 클라이언트 사용)
  const sendAudioToBackend = async (audioBlob, forcedPreviousName = null) => {
    try {
      // 이미 로딩 상태가 아니면 설정
      if (!isLoading) {
        setIsLoading(true);
      }
      
      setError(null);
      setTranscriptionResult(null);
      
      // 녹음 상태 확인하고 필요시 초기화
      if (isRecording) {
        setIsRecording(false);
      }
      
      // 전달받은 이전 운동 이름 사용 (빈 문자열이면 null로 처리)
      // 휴식 시작 버튼을 누른 경우에만 previousExerciseName이 설정되어 있음
      let currentPreviousName = forcedPreviousName !== null ? forcedPreviousName : previousExerciseName;
      if (currentPreviousName === '') {
        currentPreviousName = null;
      }
      
      // 이전 운동 이름을 workouts에서 자동으로 찾아오는 로직 제거
      // 휴식 시작 버튼을 누른 경우에만 이전 운동 이름이 설정되도록 함
      
      // FormData 생성 최적화 - async 코드 밖에서 처리
      const formData = new FormData();
      
      // 오디오 데이터가 있는지 확인
      if (!audioBlob || audioBlob.size === 0) {
        console.error("❌ 오디오 데이터가 없음");
        setError('녹음된 데이터가 없습니다. 다시 시도해주세요.');
        setIsLoading(false);
        return;
      }
      
      // iOS 관련 로그 및 데이터 유효성 확인
      
      // 바이너리 데이터 검증 - 비동기 작업이므로 간소화
      let isValidAudio = true;
      let firstBytes = '';
      
      try {
        // 최적화: 전체 데이터가 아닌 처음 20바이트만 검사
        const buffer = await audioBlob.slice(0, 20).arrayBuffer();
        const testArray = new Uint8Array(buffer);
        isValidAudio = testArray.length > 0;
        
        // 처음 몇 바이트 로깅 (디버깅용)
        firstBytes = Array.from(testArray.slice(0, 10))
          .map(b => b.toString(16).padStart(2, '0'))
          .join(' ');
      } catch (e) {
        console.error("❌ Blob 데이터 검증 오류:", e);
        isValidAudio = false;
      }
      
      if (!isValidAudio) {
        setError('오디오 데이터가 유효하지 않습니다. 다시 시도해주세요.');
        setIsLoading(false);
        return;
      }
      
      // Blob의 MIME 타입에 따라 확장자 설정
      const mimeType = audioBlob.type || 'audio/mp4';
      let fileExtension = 'mp4'; // iOS 웹뷰에서 기본값
      
      // MIME 타입에 맞는 확장자 설정
      if (mimeType.includes('webm')) {
        fileExtension = 'webm';
      } else if (mimeType.includes('mp4')) {
        fileExtension = 'mp4';
      } else if (mimeType.includes('m4a')) {
        fileExtension = 'm4a';
      } else if (mimeType.includes('3gpp')) {
        fileExtension = '3gp';
      } else if (mimeType.includes('wav')) {
        fileExtension = 'wav';
      }
      
      // 시간 기반 고유 파일명 생성
      const timestamp = new Date().getTime();
      const filename = `voice_${timestamp}.${fileExtension}`;
      
      // FormData에 데이터 추가
      formData.append('audio', audioBlob, filename);
      formData.append('previousExerciseName', currentPreviousName);
      
      // API 요청 타임아웃 설정
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30초 타임아웃
      
      try {
        // 공통 API 클라이언트(api) 사용
        const response = await api.post('/api/v1/workouts/voice', formData, {
          headers: {
            'Content-Type': 'multipart/form-data', // 이 요청에만 특정 헤더 적용
          },
          signal: controller.signal, // 타임아웃 신호 설정
        });
        
        // 타임아웃 취소
        clearTimeout(timeoutId);
        
        // 성공적인 응답 처리
        if (response.data) {
          // 응답 데이터 처리
          setTranscriptionResult(response.data);
          
          // 운동 데이터 추출
          const { exercise_name, weight, reps, duration } = response.data;
          
          // 이전 종목과 다른 새 종목인지 확인
          const isNewExercise = exercise_name && previousExerciseName && 
                              exercise_name !== previousExerciseName;
          
          // 백엔드에서 transcription이 없으면 파싱된 운동 정보로 텍스트 생성
          if (!response.data.transcription) {
            // 양손 덤벨 운동인지 확인 (원암이 포함되지 않은 덤벨 운동)
            const isBothArmsDumbellExercise = isTwoHandDumbbellExercise(exercise_name);
            
            // 무게를 조정할 변수 생성
            let adjustedWeight = weight;

            // 무게 표시 문자열 생성
            let weightText = '';
            if (adjustedWeight) { // weight 대신 adjustedWeight 사용
              // 양손 덤벨 운동인 경우 무게 2배 조정
              if (isBothArmsDumbellExercise) {
                adjustedWeight = adjustedWeight * 2;
              }
              // 조정된 무게로 포맷팅
              weightText = formatWeight(adjustedWeight, exercise_name);
            }
            
            // 시간(duration) 표시 문자열 생성
            let durationText = '';
            if (duration) {
              // 초 단위를 분 단위로 변환
              const minutes = Math.floor(duration / 60);
              const seconds = duration % 60;
              
              if (minutes > 0 && seconds > 0) {
                durationText = `${minutes}분 ${seconds}초`;
              } else if (minutes > 0) {
                durationText = `${minutes}분`;
              } else {
                durationText = `${seconds}초`;
              }
            }
            
            // 정보 배열에 있는 항목만 포함하여 문자열 생성
            const infoArray = [];
            if (weightText) infoArray.push(weightText);
            if (reps) infoArray.push(`${reps}회`);
            if (durationText) infoArray.push(durationText);
            
            // 운동 정보가 너무 적을 경우 (무게, 횟수, 시간 중 하나도 없는 경우)
            if (infoArray.length === 0) {
              // 텍스트는 설정하되 에러 메시지도 함께 표시
              const generatedText = `${exercise_name}`;
              setRecognizedText(generatedText);
              setError('인식된 운동 정보가 부족합니다. 다시 말씀해주세요.');
              setTempWorkoutData(null);
              setIsLoading(false);
              return;
            }
            
            // 텍스트 생성
            const generatedText = `${exercise_name}\n${infoArray.join(' ')}`.trim();
            setRecognizedText(generatedText);
          } else {
            // 받은 텍스트 결과 바로 설정
            setRecognizedText(response.data.transcription);
          }
          
          // 음성 인식 결과가 "잘 듣지 못했습니다"를 포함하는 경우 - recognizedText 설정 후 처리
          const transcription = response.data.transcription || '';
          if (transcription.includes('잘 듣지 못했습니다') || 
              (exercise_name && !weight && !reps && !duration)) {
            setError('운동 정보를 인식하지 못했습니다. 다시 말씀해주세요.');
            setTempWorkoutData(null);
            
            // 휴식 타이머가 활성화되어 있다면 중지하고 record 단계로 돌아감
            if (timerRef.current) {
              clearInterval(timerRef.current);
            }
            setIsResting(false);
            setWorkoutStage('record');
            
            setIsLoading(false);
            return;
          }
          
          // 새 종목이면 현재 운동 ID와 세트 수 초기화
          if (isNewExercise) {
            setCurrentWorkoutId(null);
          }
          
          // 운동 데이터를 바로 추가하지 않고 임시 상태에 저장
          setTempWorkoutData({
            exercise_name,
            weight,
            reps,
            duration
          });
          
          // 운동 데이터 수신 후 workoutStage를 record로 설정하여 휴식 단계가 보이도록 함
          setWorkoutStage('record');
          
          try {
            // 로딩 상태 유지한 채로 데이터 갱신
            await fetchTodaysWorkouts({ keepLoadingState: true });
          } finally {
            // 모든 과정 완료 후 로딩 상태 해제
            setIsLoading(false);
          }
        }
      } catch (error) {
        // 타임아웃 취소
        clearTimeout(timeoutId);
        
        console.error("백엔드 전송 오류:", error);
        
        // 사용자에게 표시할 오류 메시지
        const userMessage = error.response && error.response.data && error.response.data.message
          ? error.response.data.message
          : error.message || '음성 인식 중 오류가 발생했습니다.';
        
        setError(`요청을 처리할 수 없습니다: ${userMessage}`);
        
        // 오류 시 로딩 상태 해제
        setIsLoading(false);
      }
    } catch (outerError) {
      console.error("❌ 전체 프로세스 오류:", outerError);
      setError('요청 처리 중 오류가 발생했습니다.');
      setIsLoading(false);
    }
  };

  // 초 단위 시간을 분:초 형식으로 변환하는 함수
  const formatDuration = (seconds) => {
    if (!seconds) return null;
    
    const totalSeconds = parseInt(seconds, 10);
    if (isNaN(totalSeconds)) return null;
    
    if (totalSeconds < 60) {
      return `${totalSeconds}초`;
    }
    
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;
    
    if (remainingSeconds === 0) {
      return `${minutes}분`;
    }
    
    return `${minutes}분 ${remainingSeconds}초`;
  };

  // 새 운동 시작
  const startNewWorkout = () => {
    setIsResting(false);
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
    // tempWorkoutData 초기화 (이제 직접 백엔드에 저장하므로 필요 없음)
    setTempWorkoutData(null);
    
    setCurrentWorkoutId(null); // 새 운동이므로 ID 초기화
    setCurrentSet(0);
    setRecognizedText('');
    setPreviousExerciseName(''); // 이전 운동명 초기화
    setWorkoutStage('record'); // 기록 단계로 이동
  };

  // iOS Safari에서 오디오 자동 재생 준비
  const prepareAudioForiOS = () => {
    // 이미 처리했으면 중복 실행 방지
    if (window.__audioPrepped) return;
    
    try {
      // iOS에서는 사용자 상호작용 없이 오디오를 자동 재생하려면 먼저 빈 사운드 재생 필요
      if (isIOSDevice()) {
        const silentSound = new Audio("data:audio/mp3;base64,SUQzBAAAAAABEVRYWFgAAAAtAAADY29tbWVudABCaWdTb3VuZEJhbmsuY29tIC8gTGFTb25vdGhlcXVlLm9yZwBURU5DAAAAHQAAA1N3aXRjaCBQbHVzIMKpIE5DSCBTb2Z0d2FyZQBUSVQyAAAABgAAAzIyMzUAVFNTRQAAAA8AAANMYXZmNTcuODMuMTAwAAAAAAAAAAAAAAD/80DEAAAAA0gAAAAATEFNRTMuMTAwVVVVVVVVVVVVVUxBTUUzLjEwMFVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQsRbAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVf/zQMSkAAADSAAAAABVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVVV");
        
        // 사용자 제스처로 간주될 수 있는 이벤트에 응답하여 오디오 재생
        const playSound = () => {
          silentSound.play().catch(e => console.error("iOS 빈 사운드 재생 실패:", e));
          // 성공 플래그 설정
          window.__audioPrepped = true;
        };
        
        // 한 번만 이벤트 핸들러 실행
        const handleUserGesture = () => {
          playSound();
          document.removeEventListener('touchstart', handleUserGesture);
          document.removeEventListener('touchend', handleUserGesture);
          document.removeEventListener('click', handleUserGesture);
        };
        
        // 사용자 제스처 이벤트 리스너 추가
        document.addEventListener('touchstart', handleUserGesture);
        document.addEventListener('touchend', handleUserGesture);
        document.addEventListener('click', handleUserGesture);
      }
    } catch (e) {
      console.error("iOS 오디오 준비 중 오류:", e);
    }
  };

  // iOS 기기인지 확인 (Safari 또는 iOS WebView)
  const isIOSDevice = () => {
    // 이미 판단된 결과가 있으면 재사용
    if (window.__isIOSDevice !== undefined) {
      return window.__isIOSDevice;
    }
    
    // iOS 웹뷰인 경우
    if (window.isInIOSWebView === true || window.__iOSWebView__ === true) {
      window.__isIOSDevice = true;
      return true;
    }
    
    // Safari 브라우저인지 확인
    const isSafari = /iPad|iPhone|iPod/.test(navigator.userAgent) && 
                    !window.MSStream && 
                    /Safari/.test(navigator.userAgent) && 
                    !/Chrome/.test(navigator.userAgent);
    
    // 결과 캐싱
    window.__isIOSDevice = isSafari;
    return isSafari;
  };

  // 휴식 시작
  const startRest = async () => {
    // iOS Safari 오디오 준비
    prepareAudioForiOS();
    
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
    
    // 음성 인식 결과가 '잘 듣지 못했습니다'를 포함하는지 확인
    if (recognizedText && recognizedText.includes('잘 듣지 못했습니다')) {
      // 휴식 타이머가 활성화되어 있다면 중지하고 record 단계로 돌아감
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
      setIsResting(false);
      setWorkoutStage('record');
      
      return;
    }
    
    let exerciseData = null;
    
    // 임시 저장된 운동 데이터가 있으면 처리
    if (tempWorkoutData) {
      const { exercise_name, weight, reps, duration } = tempWorkoutData;
      
      // 무게, 횟수, 시간 중 하나도 없는 경우 진행하지 않음
      if (!weight && !reps && !duration) {
        // alert('인식된 운동 정보가 부족합니다. 다시 말씀해주세요.');
        return;
      }
      
      // 무게값이 있고 양손 덤벨 운동인 경우 무게를 2배로 계산
      let calculatedWeight = weight;
      const isBothArmsDumbellExercise = isTwoHandDumbbellExercise(exercise_name);
            
      // 양손 덤벨 운동이고 사용자가 "14kg"라고 말했으면 DB에는 28로 저장
      if (isBothArmsDumbellExercise && calculatedWeight) {
        calculatedWeight = calculatedWeight * 2;
      }
      
      exerciseData = { 
        exercise_name, 
        weight: calculatedWeight, 
        reps, 
        duration 
      };
    } 
    // 임시 데이터가 없지만 recognizedText가 있으면 파싱하여 처리
    else if (recognizedText) {
      try {
        // recognizedText 파싱 (첫 줄은 운동 이름, 두 번째 줄은 세부 정보)
        const lines = recognizedText.trim().split('\n');
        const exercise_name = lines[0];
        let weight = null;
        let reps = null;
        let duration = null;
        
        // 두 번째 줄이 있으면 세부 정보 파싱
        if (lines.length > 1) {
          const details = lines[1].split(' ');
          
          // 무게 파싱 (kg 또는 kg*2 형식)
          const weightMatch = details.find(d => d.includes('kg'));
          if (weightMatch) {
            if (weightMatch.includes('*2')) {
              // *2 형식이면 숫자 부분만 추출하고 2배
              const singleWeight = parseFloat(weightMatch.replace('kg*2', ''));
              weight = singleWeight * 2;
            } else {
              // 일반 형식이면 숫자 부분만 추출
              weight = parseFloat(weightMatch.replace('kg', ''));
              
              // 양손 덤벨 운동인 경우 명시적으로 2배 적용
              const isBothArmsDumbellExercise = isTwoHandDumbbellExercise(exercise_name);
                    
              if (isBothArmsDumbellExercise && weight) {
                weight = weight * 2;
              }
            }
          }
          
          // 횟수 파싱
          const repsMatch = details.find(d => d.includes('회'));
          if (repsMatch) {
            reps = parseInt(repsMatch.replace('회', ''), 10);
          }
          
          // 시간 파싱
          const durationMatch = details.find(d => d.includes('분'));
          if (durationMatch) {
            duration = parseInt(durationMatch.replace('분', ''), 10) * 60; // 분을 초로 변환
          }
        }
        
        // 무게, 횟수, 시간 중 하나도 없는 경우 진행하지 않음
        if (!weight && !reps && !duration) {
          // alert('인식된 운동 정보가 부족합니다. 다시 말씀해주세요.');
          return;
        }
        
        exerciseData = { exercise_name, weight, reps, duration };
      } catch (error) {
        console.error("recognizedText 파싱 오류:", error);
        // alert('운동 정보 처리 중 오류가 발생했습니다. 다시 시도해주세요.');
        return;
      }
    } else {
      // 인식된 텍스트가 없는 경우
      // alert('인식된 운동 정보가 없습니다. 마이크 버튼을 눌러 운동을 기록해주세요.');
      return;
    }
    
    // 운동 데이터가 있으면 백엔드로 전송하고 응답 받은 후 클라이언트 상태 업데이트
    if (exerciseData) {
      try {
        // user_id 추가
        if (currentUser && currentUser.id) {
          exerciseData.user_id = currentUser.id;
        }
        
        // 백엔드에 데이터 저장하고 응답 받기
        const response = await api.post('/api/v1/workouts/', exerciseData);
        
        if (!response.data || !response.data.id) {
          throw new Error('백엔드에서 유효한 ID를 반환하지 않았습니다.');
        }
        
        // 백엔드에서 받은 실제 ID
        const backendWorkoutId = response.data.id;
        
        // 현재 시간 정보
        const now = new Date().toISOString();
        
        // 실제 백엔드 ID를 사용하여 세트 정보 생성
        const newSet = {
          id: backendWorkoutId, // 백엔드에서 할당한 실제 ID 사용
          weight: exerciseData.weight, // 포맷팅하지 않은 총 무게 숫자 저장
          reps: exerciseData.reps, // 횟수 숫자 저장
          duration: exerciseData.duration, // 시간(초) 숫자 저장
          created_at: now,
          workout_id: backendWorkoutId // 명시적으로 workout_id 설정
        };
        
        // workouts 상태 업데이트 (기존 updateWorkouts 함수 대신 직접 구현)
        setWorkouts(prevWorkouts => {
          // 기존 운동 목록에서 같은 운동 이름이 있는지 확인
          const existingWorkout = prevWorkouts.find(workout => 
            workout.name === exerciseData.exercise_name);
          
          // 세트 번호 설정 (기존 운동이면 마지막 세트+1, 새 운동이면 1)
          const setNumber = existingWorkout ? existingWorkout.sets.length + 1 : 1;
          
          // 현재 세트 번호 업데이트
          setCurrentSet(setNumber);
          
          // 새 운동인 경우
          if (!existingWorkout) {
            // 새 운동 객체 생성 (이미 백엔드에 저장된 ID 사용)
            const newWorkout = {
              id: backendWorkoutId, // 세트 ID를 워크아웃 ID로도 사용
              name: exerciseData.exercise_name,
              sets: [newSet], // 위에서 생성한 세트 정보 포함
              user_id: currentUser ? currentUser.id : null,
              created_at: now,
              workout_id: backendWorkoutId // 명시적으로 workout_id 설정
            };
            
            setCurrentWorkoutId(backendWorkoutId);
            setPreviousExerciseName(exerciseData.exercise_name);
            return [...prevWorkouts, newWorkout];
          }
          
          // 기존 운동인 경우 - 세트 추가
          setPreviousExerciseName(exerciseData.exercise_name);
          return prevWorkouts.map(workout => {
            if (workout.name === exerciseData.exercise_name) {
              return {
                ...workout,
                sets: [...workout.sets, newSet] // 기존 세트 배열에 새 세트 추가
              };
            }
            return workout;
          });
        });
        
        // 임시 데이터 초기화하지 않고 유지 (UI 표시 조건에 필요)
        // setTempWorkoutData(null);
        
        // 백엔드에서 최신 데이터를 다시 가져오는 API 호출 제거
        // 클라이언트 상태가 이미 업데이트되었으므로 추가 호출 불필요
      } catch (error) {
        console.error("운동 데이터 저장 오류:", error);
        alert('운동 데이터 저장에 실패했습니다: ' + (error.message || '알 수 없는 오류'));
        return; // 오류 발생 시 휴식 시작하지 않음
      }
    }
    
    // 휴식 타이머 시작
    startRestTimer();
  };

  // 운동 마치기
  const finishWorkout = () => {
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
    
    // 상태 초기화
    setCurrentWorkoutId(null);
    setCurrentSet(0);
    setRecognizedText('');
    setPreviousExerciseName('');
    setWorkoutStage('start'); // 시작 단계로 복귀
    if (timerRef.current) {
      clearInterval(timerRef.current); // 휴식 타이머 중지
    }
    setIsResting(false);
  };

  // 휴식 시간 조정
  const adjustRestTime = (amount) => {
    const currentTime = parseInt(restTimeInput, 10) || 0;
    const newTime = Math.max(5, currentTime + amount); // 최소 5초
    setRestTimeInput(String(newTime));
    setRestTime(newTime); // 입력값 변경 시 실제 휴식 시간도 바로 업데이트
  };

  // 휴식 시간 입력 변경 핸들러
  const handleRestTimeInputChange = (e) => {
    setRestTimeInput(e.target.value);
  };

  // 휴식 시간 편집 완료 (Blur)
  const finishEditingRestTime = () => {
    handleSaveEdit('restTime', restTimeInput); // 저장 함수 호출
  };
  
  // 휴식 시간 입력 완료 (Enter)
  const handleRestTimeKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSaveEdit('restTime', restTimeInput); // 저장 함수 호출
    }
  };

  // 휴식 시간 편집 시작 (클릭)
  const startEditingRestTime = () => {
    setRestTimeInput(String(restTime)); // 현재 값으로 초기화
    setIsEditingRestTime(true);
  };

  // 휴식 시간 타이머 시작
  const startRestTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    setRemainingTime(restTime);
    setIsResting(true);
    setWorkoutStage('rest');
    
    timerRef.current = setInterval(() => {
      setRemainingTime(prevTime => {
        // iOS Safari 오디오 준비 - 3초 전 미리 로드
        if (prevTime === 3) {
          prepareAudioForiOS();
        }
        
        if (prevTime <= 1) {
          clearInterval(timerRef.current);
          setIsResting(false);
        
          // 이전 운동 이름은 이미 휴식 시작 시 설정되었으므로 여기서 다시 설정할 필요 없음
          
          setWorkoutStage('record'); // 휴식 종료 후 다시 기록 단계로
          // 알림음 재생
          playSound();
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);
  };

  // 휴식 시간 건너뛰기
  const skipRest = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
    
    // 이전 운동 이름은 이미 휴식 시작 시 설정되었으므로 여기서 다시 설정할 필요 없음
    
    setIsResting(false);
    setWorkoutStage('record'); // 즉시 다음 세트 기록 단계로
  };

  // 운동 편집 시작
  const handleEditWorkout = (workout) => {
    setEditingWorkout(workout);
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
  };

  // 세트 편집 시작
  const handleEditSet = (workout, set) => {
    setEditingWorkout(workout);
    setEditingSet(set);
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
    
    // 세트 정보 분석
    const weightMatch = set.weight ? set.weight.match(/(\d+\.?\d*)kg/) : null;
    const repsMatch = set.reps ? set.reps.match(/(\d+)회/) : null;
    const durationMatch = set.duration ? set.duration.match(/(\d+)분(?:\s+(\d+)초)?/) : null;
    
    // 운동 유형 결정
    let exerciseType = 'weight'; // 기본값
    
    if (set.weight && !set.weight.startsWith('0')) {
      exerciseType = 'weight';
    } else if (set.reps && !set.reps.startsWith('0') && (!set.duration || set.duration.startsWith('0'))) {
      exerciseType = 'bodyweight';
    } else if (set.duration && !set.duration.startsWith('0')) {
      exerciseType = 'cardio';
    }
    
    // 세트 데이터 준비
    const data = {};
    
    if (weightMatch) {
      data.weight = weightMatch[1];
    }
    
    if (repsMatch) {
      data.reps = repsMatch[1];
    }
    
    if (durationMatch) {
      const minutes = parseInt(durationMatch[1], 10);
      const seconds = durationMatch[2] ? parseInt(durationMatch[2], 10) : 0;
      data.duration = minutes * 60 + seconds;
    }
    
    // editingSet 상태 업데이트
    setEditingSet({
      workoutId: workout.id,
      setId: set.id,
      exerciseType,
      data
    });
    
    // 어떤 운동의 세트인지 식별하기 위해 workoutId 저장 (여기서는 세트를 편집하므로 운동 이름 편집은 하지 않음)
    // setEditingWorkout(null)로 변경하여 운동 이름 편집 모드가 동시에 활성화되지 않도록 함
    setEditingWorkout(null);
  };

  // 편집 저장
  const handleSaveEdit = (type, value) => {
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
    if (type === 'workoutName' && editingWorkout) {
      setWorkouts(prevWorkouts =>
        prevWorkouts.map(w =>
          w.id === editingWorkout.id ? { ...w, name: value } : w
        )
      );
      // 현재 운동명을 편집했다면 previousExerciseName 업데이트
      if (editingWorkout.id === currentWorkoutId) {
         setPreviousExerciseName(value);
      }
      setEditingWorkout(null);
    } else if (type === 'setInfo' && editingWorkout && editingSet) {
      setWorkouts(prevWorkouts =>
        prevWorkouts.map(w => {
          if (w.id === editingWorkout.id) {
            return {
              ...w,
              sets: w.sets.map(s =>
                s.id === editingSet.id ? { ...s, ...value } : s
              )
            };
          }
          return w;
        })
      );
      setEditingWorkout(null);
      setEditingSet(null);
    } else if (type === 'restTime') {
      const newRestTime = parseInt(value, 10);
      if (!isNaN(newRestTime) && newRestTime > 0) {
        setRestTime(newRestTime);
      }
      setIsEditingRestTime(false);
    }
  };

  // 편집 취소
  const handleCancelEdit = () => {
    setEditingWorkout(null);
    setEditingSet(null);
    setShowSummaryImage(false); // 요약 이미지 비활성화
    setShowSummaryCard(false); // 요약 이미지 닫기
    setIsEditingRestTime(false);
  };

  // 메뉴 토글
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  // 메뉴와 버튼 참조 생성
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  // 메뉴 외부 클릭 감지 useEffect
  useEffect(() => {
    // 클릭 이벤트 처리 함수
    const handleClickOutside = (event) => {
      // 메뉴가 열려 있고, 클릭된 요소가 메뉴(menuRef)와 버튼(menuButtonRef) 모두의 내부에 없으면 메뉴를 닫음
      if (menuOpen &&
          menuRef.current && !menuRef.current.contains(event.target) &&
          menuButtonRef.current && !menuButtonRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };

    // 메뉴가 열려있을 때만 이벤트 리스너 추가
    if (menuOpen) {
      // 'click' 대신 'mousedown'을 사용하면 클릭 시작 시점에 감지하여 더 즉각적인 반응 가능
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      // 메뉴가 닫히면 리스너 제거 (불필요한 실행 방지)
      document.removeEventListener('mousedown', handleClickOutside);
    }

    // 클린업 함수: 컴포넌트 언마운트 시 리스너 제거
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuOpen]); // menuOpen 상태가 변경될 때마다 이 effect 실행

  useEffect(() => {
    // 컴포넌트 마운트 시 오늘의 운동 기록 가져오기
    fetchTodaysWorkouts();
    
    // 가장 모드 변경 이벤트 리스너 추가
    const handleImpersonationChange = () => {
      fetchTodaysWorkouts();
    };
    
    // 페이지 로드 시 세션 상태 확인 (불필요한 API 중복 호출 제거)
    const checkSession = async () => {
      try {
        // 가장 모드 상태 확인
        const isImpersonating = sessionStorage.getItem('is_impersonating') === 'true';
        
        if (isImpersonating) {
          // 세션이 유효하지 않은 경우 API 호출만 다시 수행하고 새로고침은 하지 않음
          if (sessionStorage.getItem('session_valid') !== 'true') {
            // 새로고침 대신 데이터만 다시 가져옴
            fetchTodaysWorkouts();
          }
        }
      } catch (error) {
        console.error('세션 체크 오류:', error);
      }
    };
    
    // 페이지 로드 후 세션 상태 확인 (약간의 지연을 줘서 초기 로딩 완료 후 확인)
    const sessionCheckTimer = setTimeout(checkSession, 1000);
    
    // 이벤트 리스너 등록
    window.addEventListener('user-impersonation-changed', handleImpersonationChange);
    
    // 클린업 함수
    return () => {
      // 컴포넌트 언마운트 시 모든 상태 초기화
      isFetchingRef.current = false;
      
      // 디바운스 타이머가 있으면 취소
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
        debounceTimerRef.current = null;
      }
      
      // 세션 체크 타이머 취소
      clearTimeout(sessionCheckTimer);
      
      // 이벤트 리스너 제거
      window.removeEventListener('user-impersonation-changed', handleImpersonationChange);
    };
  }, []);

  // 오늘의 운동 기록 가져오기 (디바운싱 적용)
  const fetchTodaysWorkouts = async (options = {}) => {
    const { keepLoadingState = false } = options;
    const now = Date.now();
    
    // 이미 API 호출 중이거나 마지막 호출 후 1000ms가 지나지 않았으면 중복 호출하지 않음
    if (isFetchingRef.current || (now - lastFetchTimeRef.current < 1000)) {
      return;
    }
    
    // 기존 디바운스 타이머가 있으면 취소
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    
    // API 호출 시작 표시 - 타이머 시작 전에 설정하여 중복 호출 방지
    isFetchingRef.current = true;
    
    // 디바운스 타이머 설정 (300ms 동안 추가 호출을 무시하고 마지막 호출만 처리)
    debounceTimerRef.current = setTimeout(async () => {
      lastFetchTimeRef.current = Date.now();
      
      try {
        // 옵션에 따라 로딩 상태 설정 (음성 처리 중에는 로딩 상태를 유지하기 위함)
        if (!keepLoadingState) {
          setIsLoading(true);
        }
        
        const response = await api.get('/api/v1/workouts/today');
        
        // API 호출 성공 - 가장 모드일 경우 세션 유효성 표시
        if (sessionStorage.getItem('is_impersonating') === 'true') {
          sessionStorage.setItem('session_valid', 'true');
        }
        
        if (response.data && Array.isArray(response.data)) {
          // 응답 데이터 처리
          const processedWorkouts = processWorkoutData(response.data);
          setWorkouts(processedWorkouts);
        } else {
          // 데이터가 없거나 형식이 다른 경우 빈 배열 설정
          setWorkouts([]);
        }
        
        // 옵션에 따라 로딩 상태 해제
        if (!keepLoadingState) {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('오늘의 운동 데이터 로딩 실패:', error);
        
        // API 호출 실패 - 가장 모드일 경우 세션 유효성 표시 제거
        if (sessionStorage.getItem('is_impersonating') === 'true') {
          sessionStorage.removeItem('session_valid');
        }
        
        setWorkouts([]);
        
        // 옵션에 따라 로딩 상태 해제
        if (!keepLoadingState) {
          setIsLoading(false);
        }
      } finally {
        // API 호출 완료 표시 (성공이든 실패든)
        isFetchingRef.current = false;
        debounceTimerRef.current = null;
      }
    }, 300); // 300ms 디바운스
  };
  
  // API에서 받은 운동 데이터 처리 함수
  const processWorkoutData = (rawWorkouts) => {
    // 빈 배열이면 빈 배열 반환
    if (!rawWorkouts || rawWorkouts.length === 0) {
      return [];
    }
    
    // 먼저 모든 워크아웃을 생성 시간으로 정렬
    const sortedWorkouts = [...rawWorkouts].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateA - dateB;
    });
    
    // 같은 exercise_name을 가진 워크아웃을 그룹화
    const groupedByName = {};
    
    for (const workout of sortedWorkouts) {
      const exerciseName = workout.exercise_name || '이름 없음';
      
      if (!groupedByName[exerciseName]) {
        groupedByName[exerciseName] = {
          id: workout.id, // 첫 번째 워크아웃의 ID를 그룹 ID로 사용
          name: exerciseName,
          created_at: workout.created_at, // 첫 번째 워크아웃의 생성 시간
          sets: []
        };
      }
      
      // 세트 데이터 생성 (백엔드에서 받은 각 항목이 하나의 세트)
      const setData = {
        id: workout.id, // 세트의 실제 ID (백엔드 PK)
        created_at: workout.created_at
      };
      
      // 데이터 타입에 따라 필드 설정
      if (workout.weight) {
        // 중요: 양손 덤벨 운동 여부는 WorkoutList에서 formatWeight 호출 시 판단하므로 여기서는 원본값 저장
        setData.weight = workout.weight; 
      }
      
      if (workout.reps) {
        setData.reps = workout.reps;
      }
      
      if (workout.duration) {
        // duration은 초 단위 숫자 그대로 저장
        setData.duration = workout.duration;
      }
      
      groupedByName[exerciseName].sets.push(setData);
    }
    
    // 객체를 배열로 변환
    const result = Object.values(groupedByName);
    
    // 각 그룹 내의 세트를 생성 시간순으로 정렬
    for (const group of result) {
      group.sets.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
    }
    
    // 그룹을 생성 시간(가장 첫 세트 기준)으로 정렬
    result.sort((a, b) => {
      return new Date(a.created_at) - new Date(b.created_at);
    });
    
    return result;
  };

  // 오늘의 기록 섹션 렌더링
  const renderTodaysRecords = () => {
    return (
      <TodaysRecordSection>
        <RecordHeader>
          <RecordTitle>오늘의 기록</RecordTitle>
          <RecordDivider />
        </RecordHeader>
        
        {workouts && workouts.length > 0 && (
          <SummaryButtonContainer>
            <SummaryButton onClick={() => {
              const newState = !showSummaryCard;
              setShowSummaryCard(newState);
              setShowSummaryImage(newState);
            }}>
              <FaImage /> {showSummaryCard ? '요약 이미지 닫기' : '요약 이미지 보기'}
            </SummaryButton>
          </SummaryButtonContainer>
        )}
        
        {/* 운동 요약 이미지 */}
        {showSummaryCard && workouts && workouts.length > 0 && (
          <WorkoutSummaryImage 
            workouts={workouts} 
            user={currentUser} 
            isActive={showSummaryImage} // 상태 변수로 활성화 여부 제어
            selectedDate={new Date()} // 홈페이지는 오늘 날짜의 운동을 보여줌
          />
        )}
        
        {/* WorkoutList 컴포넌트 사용 - 이벤트 핸들러를 memoize하여 불필요한 렌더링 방지 */}
        <WorkoutList 
          workouts={workouts}
          readOnly={workoutStage === 'confirm'}
          editingWorkout={editingWorkout}
          editingSet={editingSet}
          onEditWorkoutName={handleEditWorkout}
          onEditSet={handleEditSet}
          setEditingSet={setEditingSet}
          onDataChanged={fetchTodaysWorkouts}
          api={api}
        />
      </TodaysRecordSection>
    );
  };

  // 1. 요약 이미지 토글 함수 추가
  const toggleSummaryImage = () => {
    setShowSummaryImage(prev => !prev);
  };

  // 알람 소리 URL 설정
  useEffect(() => {
    // 현재 사용자 정보에서 sound_link 확인
    if (currentUser && currentUser.sound_link) {
      // 사용자 설정 알람 소리가 있으면 오디오 요소의 src 변경
      if (audioRef.current) {
        audioRef.current.src = currentUser.sound_link;
      }
    }
  }, [currentUser]);

  // 페이지 초기화 시 데이터 로드 및 상태 초기화
  useEffect(() => {
    // 이전 운동 이름 초기화
    setPreviousExerciseName('');
    
    // 오늘의 운동 데이터 가져오기
    fetchTodaysWorkouts();
  }, []);

  // 휴식 시간 건너뛰기 함수
  const handleSkipRest = () => {
    console.log('Skipping rest...'); // 로그 추가
    // 타이머 중지
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null; // 참조 정리
    }
    // 상태 업데이트
    setIsResting(false);
    setRemainingTime(0);
    // 다음 세트 기록 단계로 전환
    setWorkoutStage('record');
  };

  // 휴식 시간 UI 렌더링 함수 (기존 로직 활용 또는 수정)
  const renderRestTimer = () => {
    if (workoutStage === 'rest' && isResting) {
      return (
        <RestTimerContainer>
          <TimerDisplay>{remainingTime}초</TimerDisplay>
          {/* 휴식 건너뛰기 버튼 추가 */}
          <SkipRestButton onClick={handleSkipRest}>
             Skip {/* 텍스트 변경 */}
          </SkipRestButton>
        </RestTimerContainer>
      );
    }
    return null;
  };

  // 기본 컨트롤 버튼 스타일 정의
  const ControlButton = styled.button`
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border: none;
    border-radius: 24px;
    padding: 0.75rem 1.5rem;
    font-size: ${({ theme }) => theme.fontSizes.md};
    font-weight: ${({ theme }) => theme.fontWeights.medium};
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: ${({ theme }) => theme.colors.primaryDark || '#b0262c'}; // 기본값 추가
    }

    &:disabled {
      background-color: ${({ theme }) => theme.colors.gray300};
      cursor: not-allowed;
    }
  `;

  // SkipRestButton 정의 (ControlButton 상속)
  const SkipRestButton = styled.button`
    text-align: center;
    font-weight: 500;
    color: ${({ theme }) => theme.colors.gray500};
    &:hover {
      color: ${({ theme }) => theme.colors.primary};
    }
  `;

  // 스타일 컴포넌트
  const HomeContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
    padding: 1rem;
    background-color: white;
    margin-top: 6rem;
    margin-left: 2rem;
    margin-right: 2rem;
    
    @media (min-width: 555px) {
      width: 400px;
      max-width: 400px;
      margin-left: auto;
      margin-right: auto;
    }
  `;

  return (
    <HomeContainer>
      <GreetingSection>
        <GreetingText>Hello, {currentUser?.nickname || currentUser?.name || 'User'}</GreetingText>
      </GreetingSection>

      <WorkoutProcessSection>
        {workoutStage === 'start' && (
          <StepItem>
            <StepHeaderRow>
              <StatusCircle active={'true'}>
                <CheckIcon>✓</CheckIcon>
              </StatusCircle>
              <StepText active={(workoutStage === 'start').toString()}>종목 시작하기</StepText>
            </StepHeaderRow>
            <StepContentContainer>
              <StartButton onClick={startNewWorkout}>
                새 종목
              </StartButton>
            </StepContentContainer>
          </StepItem>
        )}

        {workoutStage !== 'start' && (
          <>
            <StepList>
              <StepItem>
                <StepHeaderRow>
                  <StatusCircle active={'true'} completed={'true'}>
                    <CheckIcon>✓</CheckIcon>
                  </StatusCircle>
                  <StepText active={(workoutStage === 'start').toString()}>종목 시작하기</StepText>
                </StepHeaderRow>
                <StepLine completed={(!recognizedText || recognizedText.includes('잘 듣지 못했습니다')).toString()} />
              </StepItem>

              <StepItem active={(workoutStage === 'record' || workoutStage === 'confirm').toString()}>
                <StepHeaderRow>
                  <StatusCircle 
                    active={(workoutStage === 'record' || workoutStage === 'confirm').toString()}
                    completed={(workoutStage === 'rest').toString()}
                  >
                    <CheckIcon>{workoutStage === 'rest' ? '✓' : '●'}</CheckIcon>
                  </StatusCircle>
                  <StepText active={(workoutStage === 'record' || workoutStage === 'confirm').toString()}>세트 기록하기</StepText>
                </StepHeaderRow>
                {workoutStage === 'record' && (
                  <StepContentContainer>
                    <MicButtonContainer>
                      <MicButton 
                        onClick={() => {handleMicrophoneClick();}}
                        recording={isRecording.toString()}
                        disabled={isLoading}
                        recognizedText={recognizedText}
                      >
                        {isRecording ? (
                          <WaveIcon>
                            <span></span>
                            <span></span>
                            <span></span>
                            <span></span>
                          </WaveIcon>
                        ) : (
                          <FaMicrophone />
                        )}
                      </MicButton>
                    </MicButtonContainer>
                    
                    {isLoading && (
                      <LoadingContainer>
                        <LoadingSpinner />
                        <LoadingText>음성 처리 중...</LoadingText>
                      </LoadingContainer>
                    )}
                    
                    {recognizedText && !isLoading && (
                      <RecognizedText>
                        {recognizedText}
                      </RecognizedText>
                    )}
                  </StepContentContainer>
                )}
                {workoutStage === 'confirm' && (
                  <StepContentContainer>
                    <RecognizedText>
                      {recognizedText}
                    </RecognizedText>
                  </StepContentContainer>
                )}
                {workoutStage === 'rest' && (
                  <StepContentContainer>
                    <RecognizedText>
                      {recognizedText}
                    </RecognizedText>
                  </StepContentContainer>
                )}
                {recognizedText && !recognizedText.includes('잘 듣지 못했습니다') && (
                  <StepLine completed={(workoutStage !== 'rest').toString()} />
                )}
              </StepItem>

              {(workoutStage === 'rest' ||
                (workoutStage === 'record' && 
                recognizedText && 
                !recognizedText.includes('잘 듣지 못했습니다'))) && (
                <>
                  {workoutStage === 'record' && (
                    <StepItem active={'true'}>
                      <StepHeaderRow>
                        <StatusCircle active={'true'}>
                          <CheckIcon>●</CheckIcon>
                        </StatusCircle>
                        <StepText active={'true'}>세트 간 휴식하기</StepText>
                      </StepHeaderRow>
                      <StepContentContainer>
                        <ButtonGroup>
                          <RestInputContainer>
                            <RestInput>
                              <RestButton onClick={() => adjustRestTime(-5)}>-</RestButton>
                              {isEditingRestTime ? (
                                <RestTimeInput
                                  type="number"
                                  value={restTimeInput}
                                  onChange={handleRestTimeInputChange}
                                  onBlur={finishEditingRestTime}
                                  onKeyPress={handleRestTimeKeyPress}
                                  autoFocus
                                />
                              ) : (
                                <RestTimeDisplay onClick={startEditingRestTime}>
                                  {restTime}
                                </RestTimeDisplay>
                              )}
                              <RestButton onClick={() => adjustRestTime(5)}>+</RestButton>
                            </RestInput>
                            <RestTimeUnit>초</RestTimeUnit>
                          </RestInputContainer>
                        </ButtonGroup>
                        <StartRestButton onClick={startRest}>
                          휴식 시작
                        </StartRestButton>
                      </StepContentContainer>
                      <StepLine completed={'false'} />
                    </StepItem>
                  )}

                  {workoutStage === 'confirm' && (
                    <StepItem active={'true'}>
                      <StepHeaderRow>
                        <StatusCircle active={'true'}>
                          <CheckIcon>●</CheckIcon>
                        </StatusCircle>
                        <StepText active={'true'}>세트 간 휴식하기</StepText>
                      </StepHeaderRow>
                      <StepContentContainer>
                        <RestInputContainer>
                          <RestInput>
                            <RestButton onClick={() => adjustRestTime(-5)}>-</RestButton>
                            {isEditingRestTime ? (
                              <RestTimeInput
                                type="number"
                                value={restTimeInput}
                                onChange={handleRestTimeInputChange}
                                onBlur={finishEditingRestTime}
                                onKeyPress={handleRestTimeKeyPress}
                                autoFocus
                              />
                            ) : (
                              <RestTimeDisplay onClick={startEditingRestTime}>
                                {restTime}
                              </RestTimeDisplay>
                            )}
                            <RestButton onClick={() => adjustRestTime(5)}>+</RestButton>
                          </RestInput>
                          <RestTimeUnit>초</RestTimeUnit>
                        </RestInputContainer>
                        <StartRestButton onClick={startRest}>
                          휴식 시작
                        </StartRestButton>
                      </StepContentContainer>
                      <StepLine completed={'false'} />
                    </StepItem>
                  )}

                  {workoutStage === 'rest' && (
                    <StepItem active={'true'}>
                      <StepHeaderRow>
                        <StatusCircle active={'true'}>
                          <CheckIcon>●</CheckIcon>
                        </StatusCircle>
                        <StepText active={'true'}>세트 간 휴식하기</StepText>
                      </StepHeaderRow>
                      <StepContentContainer>
                        {/* CountdownDisplay 대신 renderRestTimer() 호출 */}
                        {renderRestTimer()}
                      </StepContentContainer>
                      <StepLine completed={'false'} />
                    </StepItem>
                  )}
                </>
              )}
              
              {(workoutStage === 'rest' || 
                (workoutStage === 'record' && 
                recognizedText && 
                !recognizedText.includes('잘 듣지 못했습니다'))) && (
                <StepItem active={'false'}>
                  <StepHeaderRow>
                    <StatusCircle active={'false'}>
                      <CheckIcon>●</CheckIcon>
                    </StatusCircle>
                    <StepText active={'false'}>종목 마치기</StepText>
                  </StepHeaderRow>
                  <StepContentContainer>
                    {currentSet > 0 && (
                      <CompletedSetInfo>{currentSet}세트 완료</CompletedSetInfo>
                    )}
                    <FinishWorkoutButton onClick={finishWorkout}>
                      마치기
                    </FinishWorkoutButton>
                  </StepContentContainer>
                </StepItem>
              )}
            </StepList>
          </>
        )}
      </WorkoutProcessSection>

      {renderTodaysRecords()}

      {/* SettingsButton에 menuButtonRef 연결 */}
      <SettingsButton ref={menuButtonRef} onClick={toggleMenu}>
        <FaCog />
      </SettingsButton>
      
      {menuOpen && (
        <MenuContainer ref={menuRef}>
          <MenuItem as={Link} to="/profile">
            <MenuItemText>My page</MenuItemText>
            <MenuItemIcon>👤</MenuItemIcon>
          </MenuItem>
          <MenuItem as={Link} to="/group">
            <MenuItemText>Group</MenuItemText>
            <MenuItemIcon>👥</MenuItemIcon>
          </MenuItem>
          <MenuItem as={Link} to="/dashboard">
            <MenuItemText>Dashboard</MenuItemText>
            <MenuItemIcon>📊</MenuItemIcon>
          </MenuItem>
        </MenuContainer>
      )}

      {/* 음성 알림을 위한 오디오 요소 - 기본 소리는 beep_short */}
      <audio ref={audioRef} src="https://actions.google.com/sounds/v1/alarms/beep_short.ogg" />
    </HomeContainer>
  );
};

// 스타일 컴포넌트들을 여기로 이동

const GreetingSection = styled.div`
  margin-bottom: 2rem;
`;

const GreetingText = styled.h1`
  font-size: ${({ theme }) => theme.fontSizes.xxxxl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gray900};
`;

const TodayText = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 1rem;
`;

const Section = styled.div`
  margin-bottom: 2rem;
`;

const SectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
  `;

const WorkoutListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
`;

const WorkoutItem = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: .5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const WorkoutHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.75rem;
`;

const WorkoutName = styled.div`
  background-color: ${({ theme }) => theme.colors.primary};
  padding: 0.5rem 1rem;
  color: white;
  font-weight: 500;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SetList = styled.div`  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const SetItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem;
  border-radius: 4px;
  background-color: white;
`;

const SetNumber = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.gray700};
  width: 48px;
`;

const SetInfo = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSizes.sm};
  color: ${({ theme }) => theme.colors.gray900};
`;

const SetActions = styled.div`
  display: flex;
  gap: 0.5rem;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.gray500};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const AddSetButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: transparent;
  border: 1px dashed ${({ theme }) => theme.colors.gray300};
  border-radius: 4px;
  padding: 0.5rem;
  color: ${({ theme }) => theme.colors.gray500};
  font-size: ${({ theme }) => theme.fontSizes.sm};
  cursor: pointer;
  width: 100%;
  margin-top: 0.5rem;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray50};
    color: ${({ theme }) => theme.colors.primary};
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background-color: ${({ theme }) => theme.colors.gray50};
  border-radius: 8px;
  text-align: center;
`;

const EmptyStateText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray600};
  margin-bottom: 1rem;
`;

const StartButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 24px;
  padding: .75rem 2.5rem;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  margin-bottom: 1rem;
  position: relative;
  z-index: 2;
  
  animation: borderPulse 2s ease-in-out infinite;
  
  @keyframes borderPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(222, 50, 56, 0.6);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(222, 50, 56, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(222, 50, 56, 0.6);
    }
  }
`;

const WorkoutSteps = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
`;

// StepItem 정의 수정: completed와 active prop 필터링
const StepItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !['completed', 'active'].includes(prop)
})`
  display: flex;
  flex-direction: column;
  margin-bottom: .5rem;
  position: relative;
  // opacity: ${({ active }) => (active === 'true' ? '1' : '0.6')}; // 스타일링 필요 시 여기에 active 사용
`;

const StepHeaderRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const StepLine = styled.div`
  position: absolute;
  top: 40px;
  left: 16px;
  width: 2px;
  height: calc(100% - 40px);
  background-color: transparent;
  border-left: ${({ completed, theme }) => {
    if (completed === 'true') {
      return `3px ${theme.colors.primary} dotted`;
    }
    return `2px ${theme.colors.gray300} solid`;
  }};
  z-index: 1;
`;

const StepLineShort = styled(StepLine)`
  height: 30px;
`;

const StatusCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ active, completed, theme }) => {
    if (active === 'true') {
      return completed === 'true' ? theme.colors.gray300 : theme.colors.primary;
    }
    return theme.colors.gray300;
  }};
  color: white;
  margin-right: 1rem;
  z-index: 2;
`;

const CheckIcon = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
`;

const StepText = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ active, theme }) => 
    active === 'true' ? theme.fontWeights.bold : theme.fontWeights.medium};
  color: ${({ active, theme }) => 
    active === 'true' ? theme.colors.primary : theme.colors.gray500};
`;

const ProcessButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 24px;
  padding: .75rem 2rem;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  margin-bottom: 1rem;
`;

const RecognizedText = styled.div`
  padding: 1rem 0;
  background-color: ${({ theme }) => theme.colors.gray50};
  border-radius: 8px;
  margin-top: .75rem;
  white-space: pre-wrap;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gray900};
  align-self: stretch;
`;

const MicButtonContainer = styled.div`
  position: relative;
  align-self: flex-start;
  display: flex;
  justify-content: center;
  align-items: center;
`;

// MicButton 정의 수정: recording, recognizedText prop 필터링
const MicButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['recording', 'recognizedText'].includes(prop)
})`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 120px !important;
  height: 48px;
  background-color: ${({ recording, theme }) => 
    recording === "true" ? theme.colors.gray300 : theme.colors.primary};
  color: ${({ recording, theme }) => 
    recording === "true" ? theme.colors.gray700 : 'white'};
  border: none;
  border-radius: 24px;
  font-size: 1.25rem;
  cursor: pointer;
  position: relative;
  z-index: 2;
  animation: ${({ recording, recognizedText }) => recording === "true" || recognizedText !== '' ? 'none' : 'borderPulse 2s ease-in-out infinite'};
  
  @keyframes borderPulse {
    0% {
      box-shadow: 0 0 0 0 rgba(222, 50, 56, 0.6);
    }
    50% {
      box-shadow: 0 0 0 8px rgba(222, 50, 56, 0);
    }
    100% {
      box-shadow: 0 0 0 0 rgba(222, 50, 56, 0.6);
    }
  }
`;

const RestInputContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
`;

const RestTimeUnit = styled.span`
  margin-left: 0.5rem;
  color: ${({ theme }) => theme.colors.gray700};
`;

const RestInput = styled.div`
  display: flex;
  align-items: center;
  border: 1px solid ${({ theme }) => theme.colors.gray300};
  border-radius: 8px;
  overflow: hidden;
`;

const RestButton = styled.button`
  width: 40px;
  height: 40px;
  background-color: ${({ theme }) => theme.colors.gray200};
  border: none;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  cursor: pointer;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray300};
  }
`;

const RestTimeDisplay = styled.div`
  width: 60px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const RestTimeInput = styled.input`
  width: 60px;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  border: none;
  background: transparent;
  outline: none;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  padding: 2px 0;
  
  /* number input의 화살표 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  /* Firefox에서 화살표 제거 */
  &[type=number] {
    -moz-appearance: textfield;
  }
`;

const CountdownDisplay = styled.div`
  font-size: 2.5rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  margin: 1rem 0;
  z-index: 2;
  text-align: left;
`;

const StartRestButton = styled.button`
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  border-radius: 24px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  margin-top: .5rem;
`;

const FinishWorkoutButton = styled.button`
  background-color: ${({ theme }) => theme.colors.gray300};
  color: ${({ theme }) => theme.colors.gray700};
  border: none;
  border-radius: 8px;
  padding: 0.75rem 1.5rem;
  border-radius: 24px;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
`;

const TimerDisplay = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.xxxxl};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.gray900};
  text-align: center;
  margin: 0;
`;

const SettingsButton = styled.button`
  position: fixed;
  bottom: 1rem;
  right: 1rem;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  font-size: 1.25rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  z-index: 100;
  max-width: 480px;
  margin-right: calc(50% - 240px);
  
  @media (max-width: 480px) {
    right: 1rem;
    margin-right: 0;
  }
`;

const MenuContainer = styled.div`
  position: fixed;
  bottom: 5rem;
  right: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  z-index: 99;
  max-width: 480px;
  margin-right: calc(50% - 240px);
  animation: slideUp 0.3s ease-out forwards;
  
  @keyframes slideUp {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  @media (max-width: 480px) {
    right: 1.3rem;
    margin-right: 0;
  }
`;

const MenuItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  background-color: white;
  border-radius: 24px;
  padding: 0.5rem 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  opacity: 0;
  animation: fadeIn 0.2s ease-out forwards;
  
  &:nth-child(1) {
    animation-delay: 0.15s;
  }
  
  &:nth-child(2) {
    animation-delay: 0.05s;
  }
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.gray100};
  }
`;

const MenuItemText = styled.span`
  margin-right: 0.75rem;
  color: ${({ theme }) => theme.colors.gray900};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const MenuItemIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.gray200};
`;

const ButtonGroup = styled.div`
  display: flex;
  justify-content: flex-start;
  gap: 0.5rem;
  z-index: 2;
  width: auto;
`;

const StepContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-left: 48px; /* StatusCircle 너비(32px) + margin-right(16px) */
  position: relative;
  flex: 1;
  margin-bottom: 1rem;
  
  /* 컨텐츠 내부 요소들 스타일 조정 - 버튼은 예외 처리 */
  & > * {
    align-self: flex-start;
  }
  
  /* MicButton과 다른 버튼들은 width 속성을 자체적으로 가짐 */
  & > button {
    width: initial;
  }
`;

const CompletedSetInfo = styled.div`
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray900};
  margin-bottom: 0.75rem;
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  display: flex;
  align-items: center;
  
  &::before {
    content: '';
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: ${({ theme }) => theme.colors.gray400};
    margin-right: 8px;
  }
`;

const WorkoutProcessSection = styled.div`
  margin: 1rem 0;
`;

const StepList = styled.div`
  display: flex;
  flex-direction: column;
`;

const TodaysRecordSection = styled.div`
  margin-top: 2rem;
  padding-bottom: 4rem;
  flex: 1;
`;

const RecordHeader = styled.div`
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: .75rem;
`;

const RecordTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.xl};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.gray900};
  width: fit-content;
  text-wrap: nowrap;
`;

const RecordDivider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${({ theme }) => theme.colors.gray900};
`;

// 새로운 웨이브 아이콘 컴포넌트
const WaveIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  
  span {
    display: inline-block;
    width: 3px;
    height: 8px;
    margin: 0 1px;
    background-color: ${({ theme }) => theme.colors.gray700};
    border-radius: 3px;
    
    &:nth-child(1) {
      animation: wave 1s ease-in-out infinite;
    }
    &:nth-child(2) {
      animation: wave 1s ease-in-out 0.2s infinite;
    }
    &:nth-child(3) {
      animation: wave 1s ease-in-out 0.4s infinite;
    }
    &:nth-child(4) {
      animation: wave 1s ease-in-out 0.6s infinite;
    }
  }
  
  @keyframes wave {
    0%, 100% {
      height: 8px;
    }
    50% {
      height: 16px;
    }
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-top: 1rem;
  width: 100%;
  gap: .5rem;
`;

const LoadingText = styled.div`
  // margin-top: 0.5rem;
  font-size: ${({ theme }) => theme.fontSizes.md};
  color: ${({ theme }) => theme.colors.gray700};
`;

const LoadingSpinner = styled.div`
  width: 30px;
  height: 30px;
  border: 3px solid ${({ theme }) => theme.colors.gray200};
  border-top: 3px solid ${({ theme }) => theme.colors.primary};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const SummaryButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 1rem;
`;

const SummaryButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  background-color: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: 24px;
  padding: 0.75rem 1.5rem;
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primaryDark || '#b0262c'};
  }
`;

const RestTimerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
`;

export default HomePage;

